import { Suspense, useImperativeHandle, useMemo, useRef } from "react";

import {
  lazyWithBackgroundWarm,
  warmChunkNow,
} from "@client/utils/backgroundChunkWarming";
import { cn } from "@client/utils/cn";
import { resolveVideoPosterUrl } from "@client/utils/videoPosterUrl";

import type { VideoPlayerHandle, VideoPlayerProps } from "./VideoPlayer";

// The public face of the video player. The real player — the engine and all of
// its layouts/menus — is several hundred KB of startup parse, and it sat in
// the entry bundle because landing-visible surfaces (the funder workspace's
// clips view, meeting cards) import the `components/video` barrel. This
// wrapper keeps the barrel light: the heavy module loads through lazy() on
// its own chunk — preloaded from the production HTML shell on funder review
// landings, or warmed here during client-side review navigation — while the
// fallback holds the player's exact box (the same wrapper element/classes the
// player root uses) so there is no layout shift and the brief pre-chunk state is
// indistinguishable from the player's own black-before-poster loading state.
// Same pattern as the note renderer's MarkdownViewer/MarkdownViewerImpl split.

const loadVideoPlayer = async () => ({
  default: (await import("./VideoPlayer")).VideoPlayer,
});

const LazyVideoPlayer = lazyWithBackgroundWarm(loadVideoPlayer);

/**
 * Client-side complement to the production shell's route-scoped modulepreload:
 * start fetching the shared player chunk before a selected video's catalog
 * data is ready. This still owns dev loads, in-document navigation into
 * Review, and the safe fallback when manifest resolution was unavailable.
 *
 * Loads through the component's `preload` (the tracked loader), NOT the raw
 * loader. The raw loader dedupes the network fetch but never fills
 * lazyWithBackgroundWarm's synchronous render cache, so the player's first
 * render still suspended and React committed it in a later transition-priority
 * retry — measured on staging as the element (and its media request) landing
 * ~110ms after the first workspace commit it should have been part of. Through
 * `preload`, a warm that resolves before that commit renders the player
 * synchronously inside it.
 *
 * This is intentionally an explicit, route-owned preload rather than making
 * every VideoPlayer import eager. Admin grids and non-media routes should keep
 * the heavy player module off their startup path.
 *
 * Routed through warmChunkNow (not a bare import + catch) because in
 * production a failed chunk import surfaces as a vite:preloadError event, not
 * a rejection: main.tsx's handler answers any failure the warm bookkeeping
 * doesn't claim with a page reload. warmChunkNow records this speculative
 * load's failure for the handler to claim, so a flaky preload can never
 * reload the app out from under the user; a rendered LazyVideoPlayer retains
 * the normal on-demand load and reload-recovery behavior.
 */
export function preloadVideoPlayer(): void {
  warmChunkNow(LazyVideoPlayer.preload);
}

/**
 * A handle facade that buffers imperative calls until the real player mounts.
 *
 * The lazy boundary must not break the handle contract callers were built
 * against: before the split the player mounted synchronously, so a parent's
 * ref was attached before its own effects ran, and mount-time one-shot
 * effects (e.g. the overview panel's transcript deep-link seek, which latches
 * an "applied" flag and never retries) could call `seekTo` unconditionally.
 * While the chunk is still loading, calls buffer — last seek wins, play/pause
 * toggle an intent flag — and replay (position first, then play, the order
 * seek-and-play callers use) the moment the player attaches.
 */
export function createBufferedVideoPlayerHandle(): {
  facade: VideoPlayerHandle;
  attach: (handle: VideoPlayerHandle | null) => void;
  /** Drop buffered calls — they were issued for a video/clip no longer shown. */
  reset: () => void;
} {
  let inner: VideoPlayerHandle | null = null;
  // `intent` is the last play/pause the caller asked for, replayed AFTER the
  // seek. A pause must REPLAY rather than merely cancel a pending play, so
  // seek-then-pause ends paused the way it did against the old synchronous
  // handle.
  let pending: {
    seek?: number;
    seekOpts?: { suppressProgressWrite?: boolean };
    intent?: "play" | "pause";
  } = {};
  return {
    reset() {
      pending = {};
    },
    facade: {
      seekTo(seconds: number, opts?: { suppressProgressWrite?: boolean }) {
        // The options ride the queue with the seek — dropping them here
        // would strip a navigation seek's progress-write suppression exactly
        // when it matters (a landing issued before the chunk loads).
        if (inner) inner.seekTo(seconds, opts);
        else {
          pending.seek = seconds;
          pending.seekOpts = opts;
        }
      },
      play() {
        if (inner) inner.play();
        else pending.intent = "play";
      },
      pause() {
        if (inner) inner.pause();
        else pending.intent = "pause";
      },
      getCurrentTime() {
        return inner?.getCurrentTime() ?? pending.seek ?? 0;
      },
    },
    attach(handle: VideoPlayerHandle | null) {
      inner = handle;
      if (handle == null) return;
      const queued = pending;
      pending = {};
      if (queued.seek != null) handle.seekTo(queued.seek, queued.seekOpts);
      if (queued.intent === "play") handle.play();
      else if (queued.intent === "pause") handle.pause();
    },
  };
}

export function VideoPlayer(props: VideoPlayerProps) {
  // The facade is attached to the caller's ref at the SHELL's layout-effect
  // time — the same timing the pre-split player attached at — so parents'
  // mount-time effects always see a non-null handle.
  const { facade, attach, reset } = useMemo(
    createBufferedVideoPlayerHandle,
    [],
  );
  useImperativeHandle(props.ref, () => facade, [facade]);

  // Buffered calls are scoped to the video they were issued for. Callers reuse
  // one <VideoPlayer> position across selections (the clips rail swaps the
  // selected video in place), so a seek queued for video A while the chunk
  // loads must not replay against video B. Pre-split, that couldn't happen:
  // calls landed on A's player immediately. Render-time compare, same idiom the
  // player itself uses for videoId changes.
  const lastVideoIdRef = useRef(props.videoId);
  if (lastVideoIdRef.current !== props.videoId) {
    lastVideoIdRef.current = props.videoId;
    reset();
  }

  const eagerPosterUrl = props.hasPoster
    ? resolveVideoPosterUrl({
        videoId: props.videoId,
        hasPoster: true,
        posterId: props.posterId,
        posterCdnUrl: props.posterCdnUrl,
      })
    : null;

  return (
    <Suspense
      fallback={
        <div
          // The shell stands in for the player and must carry the SAME
          // intrinsic sizing as the root it becomes, or the box changes size
          // when the chunk lands: the player root has an aspect-video fallback
          // (its container-type: size never sizes from content, so a bare caller
          // in auto-height flow would otherwise be 0px during load).
          className={cn(
            "relative aspect-video h-full w-full bg-black",
            props.className,
          )}
        >
          {eagerPosterUrl != null && (
            <img
              src={eagerPosterUrl}
              alt=""
              loading="eager"
              fetchPriority="high"
              // Match the player's poster, which inherits its
              // crossOrigin="anonymous": a CORS-mode and a no-cors request for
              // the same URL can't share an in-flight fetch, so without this
              // the hero poster downloaded twice on every workspace load.
              // Same rule on every poster <img> — see VideoPoster.tsx.
              crossOrigin="anonymous"
              draggable={false}
              className="absolute inset-0 size-full object-contain"
            />
          )}
        </div>
      }
    >
      <LazyVideoPlayer {...props} ref={attach} />
    </Suspense>
  );
}
